import { EditOutlined, GetAppOutlined } from '@mui/icons-material';
import {
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  CircularProgress,
  Collapse,
} from '@mui/material';

import { useRouter } from 'next/navigation';

import { useState } from 'react';

import type {
  FilePermission,
  OwnerPermisson,
} from '@/modules/files/hooks/useFilePermissions';
import type { DocumentType } from '@/gql';
import { graphql } from '@/gql';

import { downloadFile } from '@/utils/file/fileUtils';

import { AI_ASSISTANT_SUPPORTED_FILE_TYPES } from '@/utils/file/aiAssistantSupportedFileTypes';

import { ConfirmDownloadDialog } from '../../../dialogs/ConfirmDownloadDialog';

import type { OwnerCandidate } from '../FileOwnerSelector';

import { OfficeOnlineIcon } from './option-items/OfficeOpenIcon';
import { MaybeUsableMenuItem } from './option-items/MaybeUnusableMenuItem';
import { EditLinkOrNameMenuItem } from './option-items/EditLinkOrNameMenuItem';
import { DeleteFileMenuItem } from './option-items/DeleteFileMenuItem';
import { PinFileMenuItem } from './option-items/PinFileMenuItem';
import { UnpinFileMenuItem } from './option-items/UnpinFileMenuItem';
import { LockFileMenuItem } from './option-items/LockFileMenuItem';
import { UnlockFileMenuItem } from './option-items/UnlockFileMenuItem';
import { SignFileMenuItem } from './option-items/SignFileMenuItem';
import { UnsignFileMenuItem } from './option-items/UnsignFileMenuItem';
import { DuplicateFileMenuItem } from './option-items/DuplicateFileMenuItem';
import { ReviewDateFileMenuItem } from './option-items/ReviewDateFileMenuItem';
import { DisableDownloadOfFileMenuItem } from './option-items/DisableDownloadOfFileMenuItem';
import { EnableDownloadOfFileMenuItem } from './option-items/EnableDownloadOfFileMenuItem';
import { MoveFileMenuItem } from './option-items/MoveFileMenuItem';
import { CopyLinkToFileMenuItem } from './option-items/CopyLinkToFileMenuItem';
import { OpenFileInNewTabMenuItem } from './option-items/OpenFileInNewTabMenuItem';
import { ShareFileOnNoticeboardMenuItem } from './option-items/ShareFileToNoticeboardMenuItem';
import { AskAiAssistantMenuItem } from './option-items/AskAiAssistantMenuItem';

const FILE_OPTION_ITEMS_FILE_FRAGMENT = graphql(`
  fragment fileOptionItemsFileFields on File {
    ...pinFileMenuItemFileFields
    ...unpinFileMenuItemFileFields
    ...lockFileMenuItemFileFields
    ...unlockFileMenuItemFileFields
    ...signFileMenuItemFileFields
    ...unsignFileMenuItemFileFields
    ...duplicateFileMenuItemFileFields
    ...fileUrlDialogFileFields
    ...openFileInNewTabMenuItemFileFields
    ...copyLinkToFileMenuitemFileFields
    ...askAiAssistantMenuItemFileFields
  }
`);

const FILE_OPTION_ITEMS_FILES_FOR_OWNER_FRAGMENT = graphql(`
  fragment fileOptionItemsFilesForOwnerFields on FilesForOwner {
    ...fileUrlDialogFilesForOwnerFields
  }
`);

interface Props {
  file: DocumentType<typeof FILE_OPTION_ITEMS_FILE_FRAGMENT>;
  filesForOwner: DocumentType<
    typeof FILE_OPTION_ITEMS_FILES_FOR_OWNER_FRAGMENT
  >;
  officeOnlineEditorApplication: string | null | undefined;
  permission: FilePermission;
  ownerPermission: OwnerPermisson | null;
  ownerCandidates: OwnerCandidate[];
  rootOwnerId: string;
  loading: boolean;
  onClose: () => void;
  openRenameFile: () => void;
}

export const FileOptionItems = ({
  file,
  filesForOwner,
  officeOnlineEditorApplication,
  permission,
  ownerPermission,
  ownerCandidates,
  rootOwnerId,
  loading,
  onClose,
  openRenameFile,
}: Props) => {
  const router = useRouter();
  const [confirmDownloadOpen, setConfirmDownloadOpen] = useState(false);

  const { _id: fileId, folder, url, adminLock, pinned, preventDownload } = file;

  const {
    canEdit,
    canShare,
    canLockRoot,
    canLock,
    canSign,
    canUnsignCount,
    canPreventDownload,
    canPreventDownloadRoot,
  } = permission;

  const folderOwner = ownerCandidates.find(
    (candidate) => candidate._id === file?.owner?._id,
  );

  const unusableExplanation =
    folderOwner && folderOwner.name
      ? `You need to be an admin of ${folderOwner.name} to do this`
      : null;

  const fileExtension = file.filename
    ? file.filename.split('.').pop()?.toLowerCase()
    : '';

  const isSupportedFileType = fileExtension
    ? AI_ASSISTANT_SUPPORTED_FILE_TYPES.includes(fileExtension)
    : false;

  return (
    <div>
      {canShare && (
        <ShareFileOnNoticeboardMenuItem
          file={file}
          filesForOwner={filesForOwner}
          onClose={onClose}
        />
      )}
      {isSupportedFileType && (
        <AskAiAssistantMenuItem file={file} onClose={onClose} />
      )}
      {!(url || preventDownload) && (
        <MenuItem
          onClick={() => {
            onClose();
            setConfirmDownloadOpen(true);
          }}
        >
          <ListItemIcon>
            <GetAppOutlined />
          </ListItemIcon>
          <ListItemText primary="Download" />
        </MenuItem>
      )}
      <ConfirmDownloadDialog
        open={confirmDownloadOpen}
        onClose={() => setConfirmDownloadOpen(false)}
        onConfirm={() => {
          if (fileId) {
            downloadFile(fileId);
          }
        }}
      />
      <CopyLinkToFileMenuItem file={file} onClose={onClose} />
      {folder && <OpenFileInNewTabMenuItem file={file} onClose={onClose} />}
      {canEdit && <Divider />}
      <Collapse in={Boolean(officeOnlineEditorApplication)}>
        <MenuItem
          disabled={loading}
          onClick={() => {
            onClose();
            router.push(`/document/${fileId}/edit`);
          }}
        >
          <ListItemIcon>
            {officeOnlineEditorApplication ? (
              <OfficeOnlineIcon application={officeOnlineEditorApplication} />
            ) : (
              <CircularProgress size={20} />
            )}
          </ListItemIcon>
          <ListItemText
            primary={
              loading || !officeOnlineEditorApplication
                ? 'Loading'
                : `Edit ${
                    officeOnlineEditorApplication
                      ? `in ${officeOnlineEditorApplication}`
                      : ''
                  } for the web`
            }
          />
        </MenuItem>
      </Collapse>
      {url ? (
        <EditLinkOrNameMenuItem
          file={file}
          filesForOwner={filesForOwner}
          canEdit={canEdit}
          canLockRoot={canLockRoot}
          unusableExplanation={unusableExplanation}
          onClose={onClose}
        />
      ) : (
        <MaybeUsableMenuItem
          isUsable={canEdit}
          isExpectedUsable={canLockRoot}
          unusableExplanation={unusableExplanation}
          onClick={() => {
            openRenameFile();
            onClose();
          }}
        >
          <ListItemIcon>
            <EditOutlined />
          </ListItemIcon>
          <ListItemText primary="Rename" />
        </MaybeUsableMenuItem>
      )}
      <MoveFileMenuItem
        file={file}
        filesForOwner={filesForOwner}
        ownerCandidates={ownerCandidates}
        ownerPermission={ownerPermission}
        rootOwnerId={rootOwnerId}
        canEdit={canEdit}
        canLockRoot={canLockRoot}
        unusableExplanation={unusableExplanation}
        onClose={onClose}
      />
      {!folder && !url && (
        <DuplicateFileMenuItem
          file={file}
          rootOwnerId={rootOwnerId}
          canEdit={canEdit}
          canLockRoot={canLockRoot}
          unusableExplanation={unusableExplanation}
          onClose={onClose}
        />
      )}
      {canEdit && <Divider />}
      {pinned ? (
        <UnpinFileMenuItem
          file={file}
          canEdit={canEdit}
          canLockRoot={canLockRoot}
          unusableExplanation={unusableExplanation}
          onClose={onClose}
        />
      ) : (
        <PinFileMenuItem
          file={file}
          canEdit={canEdit}
          canLockRoot={canLockRoot}
          unusableExplanation={unusableExplanation}
          onClose={onClose}
        />
      )}
      {adminLock ? (
        <UnlockFileMenuItem
          file={file}
          canLock={canLock}
          canLockRoot={canLockRoot}
          unusableExplanation={unusableExplanation}
          onClose={onClose}
        />
      ) : (
        <LockFileMenuItem
          file={file}
          canLock={canLock}
          canLockRoot={canLockRoot}
          unusableExplanation={unusableExplanation}
          onClose={onClose}
        />
      )}
      {!folder && (
        <ReviewDateFileMenuItem
          file={file}
          canEdit={canEdit}
          canLockRoot={canLockRoot}
          unusableExplanation={unusableExplanation}
          onClose={onClose}
        />
      )}
      {Boolean(!folder && !url && canSign) && (
        <SignFileMenuItem file={file} onClose={onClose} />
      )}
      {Boolean(!folder && !url && canUnsignCount > 0) && (
        <UnsignFileMenuItem
          file={file}
          canUnsignCount={canUnsignCount}
          onClose={onClose}
        />
      )}
      {!folder &&
        !url &&
        (preventDownload ? (
          <EnableDownloadOfFileMenuItem
            file={file}
            canPreventDownload={canPreventDownload}
            canPreventDownloadRoot={canPreventDownloadRoot}
            unusableExplanation={unusableExplanation}
            onClose={onClose}
          />
        ) : (
          <DisableDownloadOfFileMenuItem
            file={file}
            canPreventDownload={canPreventDownload}
            canPreventDownloadRoot={canPreventDownloadRoot}
            unusableExplanation={unusableExplanation}
            onClose={onClose}
          />
        ))}
      {canEdit && <Divider />}
      <DeleteFileMenuItem
        file={file}
        canEdit={canEdit}
        canLockRoot={canLockRoot}
        unusableExplanation={unusableExplanation}
        onClose={onClose}
      />
    </div>
  );
};
